<template>
  <div>
    <wc-data-box
      mini
      title='Line Items For:'
      :loading='isLoading'
      :showHelp='wo.office_complete'
      helpText='This work order has been processed by the office and can no longer be edited'
    >
      <template v-slot:appendTitle>
        <span class='ml-2 text-uppercase font-weight-bold'>{{ woInfoTitle }}</span>
        <v-chip
          v-if='wo.project && !outsideWarranty && wo.project_obj.project_detail.is_capital_system && wo.project_obj.project_detail.install_date'
          xSmall
          pill
          color='green'
          dark
          class='ml-3'
        >
          Install date: {{ wo.project_obj.project_detail.install_date | shortDate }}
        </v-chip>
        <v-chip
          v-if='outsideWarranty'
          xSmall
          pill
          color='error'
          dark
          class='ml-3'
        >
          Past Warranty - Installed: {{ wo.project_obj.project_detail.install_date | shortDate }}
        </v-chip>
        <v-chip
          v-if='wo.project && wo.project_obj.project_detail.is_capital_system === false'
          xSmall
          pill
          color='purple'
          dark
          class='ml-3'
        >
          System Takeover - Acquired: {{ wo.project_obj.project_detail.install_date | shortDate }}
        </v-chip>
      </template>
      <template v-slot:toolbarButtons v-if='!woOverviewPage'>
        <v-btn
          x-small
          elevation='0'
          color='primary'
          @click='openWO'
          :disabled='isLoading'
        >Open WO
        <v-icon x-small style='margin-top: 2px; margin-left: 8px;'>mdi-open-in-new</v-icon>
      </v-btn>
      </template>
      <v-simple-table dense>
        <thead>
          <tr class='overline'>
            <th class='text-left' :class='inventoryIcon.color + "--text"'>Product</th>
            <th class='text-right' :class='inventoryIcon.color + "--text"' width='75'>Qty</th>
            <th class='text-right' :class='inventoryIcon.color + "--text"' width='120'>$/Unit</th>
            <th class='text-right' :class='inventoryIcon.color + "--text"' width='75'>Wrty</th>
            <th class='text-right' :class='inventoryIcon.color + "--text"' width='83'>Total</th>
            <th class='text-right' :class='inventoryIcon.color + "--text"' width='120'></th>
          </tr>
        </thead>
        <tbody>
          <wo-processing-product-line
            v-for='(l, index) in productLines'
            :key='index'
            :line='l'
            :product='product'
            :fontColor='inventoryIcon.color'
            :disableEdit='wo.office_complete'
            @save='saveProductLine'
            @deleteLine='confirmProductDelete'
          ></wo-processing-product-line>
          <wo-processing-product-line
            v-if='showProductDialog'
            :line='productLineCopy'
            :editMode='showProductDialog'
            :product='product'
            @save='saveProductLine'
            @cancelCreate='showProductDialog = false'
            @deleteLine='confirmProductDelete'
          ></wo-processing-product-line>
          <tr v-if='!showProductDialog'>
            <td colspan='4'>
              <v-btn
                v-if='!wo.office_complete && product.length'
                width='105px'
                x-small
                depressed
                dark
                :color='inventoryIcon.color'
                @click='addProductLine()'
              >
                Add Product
              </v-btn>
              <span v-if='!product.length' class='caption'>No items in CPM (please add in Assets module)</span>
            </td>
            <td class='text-right font-weight-bold' :class='inventoryIcon.color + "--text"'>
              {{ productTotal | decMoney }}
            </td>
            <td></td>
          </tr>
        </tbody>
      </v-simple-table>

      <v-divider></v-divider>

      <v-simple-table dense>
        <thead>
          <tr class='overline'>
            <th class='text-left' :class='labourIcon.color + "--text"' width='70'>Lead</th>
            <th class='text-left' :class='labourIcon.color + "--text"'>Task Completed</th>
            <th class='text-right' :class='labourIcon.color + "--text"' width='75'>mins</th>
            <th class='text-right' :class='labourIcon.color + "--text"' width='75'>wkrs</th>
            <th class='text-right' :class='labourIcon.color + "--text"' width='120'>rate/hr</th>
            <th class='text-right' :class='labourIcon.color + "--text"' width='75'>Wrty</th>
            <th class='text-right' :class='labourIcon.color + "--text"' width='83'>Total</th>
            <th class='text-right' :class='labourIcon.color + "--text"' width='120'></th>
          </tr>
        </thead>
        <tbody>
          <wo-processing-labour-line
            v-for='(l, index) in labourLines'
            :key='index'
            :line='l'
            :labour='labour'
            :fontColor='labourIcon.color'
            :disableEdit='wo.office_complete'
            @save='saveLabourLine'
            @deleteLine='confirmLabourDelete'
            @showError='showError'
          ></wo-processing-labour-line>
          <wo-processing-labour-line
            v-if='showLabourDialog'
            :line='labourLineCopy'
            :editMode='showLabourDialog'
            :labour='labour'
            @save='saveLabourLine'
            @cancelCreate='showLabourDialog = false'
            @deleteLine='confirmLabourDelete'
            @showError='showError'
          ></wo-processing-labour-line>
          <tr v-if='!showLabourDialog'>
            <td colspan='6'>
              <v-btn
                v-if='!wo.office_complete && labour.length'
                width='105px'
                x-small
                depressed
                dark
                :color='labourIcon.color'
                @click='addLabourLine()'
              >
                Add Labour
              </v-btn>
              <span v-if='!labour.length' class='caption'>No items in CPM (please add in Assets module)</span>
            </td>
            <td class='text-right font-weight-bold' :class='labourIcon.color + "--text"'>
              {{ labourTotal | decMoney }}
            </td>
            <td></td>
          </tr>
        </tbody>
      </v-simple-table>
    </wc-data-box>

    <div class='body-2 mt-4 font-weight-bold text-center error--text' v-if='errorMessage !== ""'>{{ errorMessage }}</div>

    <wc-dialog
      :showDialog='showConfirmProductDelete'
      title='Please confirm'
      icon='mdi-alert'
      iconColor='error'
      @cancel='showConfirmProductDelete = false'
      width='350'
      saveText='Yes, Delete'
      saveColor='error'
      @save='deleteProductLine'
    >
      <br>
      Are you sure you want to delete this product line?<br>
      <br>
      <!-- <strong>{{ productLineCopy.product_obj.description }}</strong> -->
    </wc-dialog>

    <wc-dialog
      :showDialog='showConfirmLabourDelete'
      title='Please confirm'
      icon='mdi-alert'
      iconColor='error'
      @cancel='showConfirmLabourDelete = false'
      width='350'
      saveText='Yes, Delete'
      saveColor='error'
      @save='deleteLabourLine'
    >
      <br>
      Are you sure you want to delete this labour line?<br>
      <br>
      <strong v-if='labourLineCopy.added_by'>{{ 'Added by: ' + labourLineCopy.added_by_obj.first_name }}</strong><br>
      <span>{{ labourLineCopy.task_completed }}</span>
    </wc-dialog>

  </div>
</template>

<script>
import WOProcessingLabourLine from '@/modules/wos/components/WOProcessingLabourLine'
import WOProcessingProductLine from '@/modules/wos/components/WOProcessingProductLine'
// import wosAPI from '@/modules/wos/api/index'
import { mapState } from 'vuex'
import moment from 'moment'
import _ from 'lodash'

export default {
  name: 'work-order-processing-line-items',
  props: {
    wo: {
      required: true
    },
    product: {
      required: true
    },
    labour: {
      required: true
    },
    productLines: {
      required: true
    },
    labourLines: {
      required: true
    },
    isSavingLine: {
      required: false,
      type: Boolean,
      default: false
    },
    isLoading: {
      required: false,
      type: Boolean,
      default: false
    },
    woOverviewPage: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    showConfirmProductDelete: false,
    showConfirmLabourDelete: false,
    showProductDialog: false,
    showLabourDialog: false,
    showHelp: false,
    productLineCopy: {
      work_order: '',
      added_by: '',
      is_warranty: false,
      product: '',
      product_obj: {
        description: '',
        service_price: 0
      },
      qty: '1',
    },
    labourLineCopy: {
      work_order: '',
      added_by: '',
      added_by_obj: {
        first_name: ''
      },
      date: '',
      is_warranty: false,
      labour_type: '',
      labour_type_obj: {
        description: '',
        service_price: 0
      },
      num_workers: 1,
      num_mins: 10,
      task_completed: '',
    },
    errorMessage: ""
  }),
  computed: {
    ...mapState({
      inventoryIcon: state => state.icons.modules.inventory,
      labourIcon: state => state.icons.modules.labour
    }),
    woInfoTitle () {
      if (this.wo && this.wo.project) {
        return this.wo.number + ' - ' + this.wo.project_obj.name + ' - ' + this.wo.project_obj.site_address 
      }
      return this.wo.number + ' - ' + this.wo.estimate_obj.name + ' - ' + this.wo.estimate_obj.site_address 
    },
    outsideWarranty () {
      if (this.wo.project && this.wo.project_obj.project_detail.install_date && this.wo.project_obj.project_detail.is_capital_system) {
        let fiveYearsAgo = moment().subtract(5, 'years')
        let oneYearAgo = moment().subtract(1, 'years')
        let overFive = moment(this.wo.project_obj.project_detail.install_date, 'YYYY-MM-DD').isBefore(fiveYearsAgo)
        let overOne = moment(this.wo.project_obj.project_detail.install_date, 'YYYY-MM-DD').isBefore(oneYearAgo)
        if (this.wo.project_obj.is_comm) {
          return (overOne || !this.wo.project_obj.project_detail.is_capital_system)
        }
        return (overFive || !this.wo.project_obj.project_detail.is_capital_system)
      }
      return false
    },
    productTotal () {
      let tmp = 0
      this.productLines.forEach(l => tmp += l.is_warranty ? 0 : l.product_obj.service_price * l.qty)
      return tmp
    },
    labourTotal () {
      let tmp = 0
      this.labourLines.forEach(l => tmp += l.is_warranty ? 0 : (l.labour_type_obj.service_price * (l.num_mins * l.num_workers / 60) ) )
      return tmp
    },
    today () {
      return moment().format('YYYY-MM-DD').toString()
    }
  },
  methods: {
    async addProductLine () {
      await this.resetProductLineCopy()
      this.showProductDialog = true
    },
    async addLabourLine () {
      await this.resetLabourLineCopy()
      this.showLabourDialog = true
    },
    resetLabourLineCopy () {
      this.labourLineCopy = {
        work_order: this.wo.id,
        added_by: this.$store.state.auth.profile.id,
        added_by_obj: {
          first_name: this.$store.state.auth.profile.first_name
        },
        date: this.today,
        is_warranty: false,
        labour_type: '',
        labour_type_obj: {
          description: '',
          service_price: 0
        },
        num_workers: 1,
        num_mins: 10,
        task_completed: ''
      }
    },
    resetProductLineCopy () {
      this.productLineCopy = {
        work_order: this.wo.id,
        added_by: this.$store.state.auth.profile.id,
        is_warranty: false,
        product: '',
        product_obj: {
          description: '',
          service_price: 0
        },
        qty: 1,
      }
    },
    async saveProductLine (line) {
      this.$emit('saveProductLine', line)
      this.showProductDialog = false
      this.resetProductLineCopy()
    },
    async saveLabourLine (line) {
      this.$emit('saveLabourLine', line)
      this.showLabourDialog = false
      this.resetLabourLineCopy()
    },
    confirmProductDelete (line) {
      this.productLineCopy = _.cloneDeep(line)
      this.showConfirmProductDelete = true
    },
    confirmLabourDelete (line) {
      this.labourLineCopy = _.cloneDeep(line)
      this.showConfirmLabourDelete = true
    },
    async deleteProductLine () {
      this.$emit('deleteProductLine', this.productLineCopy)
      this.resetProductLineCopy()
      this.showConfirmProductDelete = false
    },
    async deleteLabourLine () {
      this.$emit('deleteLabourLine', this.labourLineCopy)
      this.resetProductLineCopy()
      this.showConfirmLabourDelete = false
    },
    openWO () {
      const link = document.createElement('a')
      const invURL = this.$router.resolve({ name: 'WOOverview', params: { id: this.wo.id} })
      link.target = '_blank'
      link.href = invURL.href
      link.click()
    },
    showError (message) {
      this.errorMessage = message
    }
  },
  components: {
    'wo-processing-labour-line': WOProcessingLabourLine,
    'wo-processing-product-line': WOProcessingProductLine,

  }
}
</script>
